import React, { memo, ReactNode, useMemo } from 'react';
import { cn } from 'lib/utils';
import { Label } from 'components/ui/label';

interface Props {
  label: string;
  value: string | ReactNode;
  small?: boolean;
  icon?: React.ComponentType<any>;
  ellipsis?: boolean;
  displayedLength?: number;
  className?: string;
  mobileLayout?: boolean;
}

function Info({
  label,
  value,
  displayedLength,
  small = false,
  ellipsis = false,
  icon: Icon,
  className,
  mobileLayout = false,
}: Props) {
  const displayedValue = useMemo(() => {
    if (typeof value === 'string' && displayedLength) {
      return value.slice(0, displayedLength) + '...';
    }
    return value;
  }, [displayedLength, value]);

  return (
    <div
      className={cn(
        'w-full p-3 sm:p-4 flex flex-col items-start gap-2 sm:gap-4 bg-background border rounded-lg max-w-full overflow-hidden',
        {
          'sm:flex-row sm:items-center': !mobileLayout,
          'flex-col': mobileLayout,
        },
        className
      )}
    >
      {Icon && (
        <div className="flex-grow-0 flex-shrink-0 text-primary">
          <Icon />
        </div>
      )}
      <div className="flex-grow w-auto flex-shrink min-w-0">
        <Label
          className={cn(
            'font-normal m-0 mb-1 text-muted-foreground block',
            small ? 'text-xs sm:text-sm' : 'text-xs sm:text-base'
          )}
        >
          {label}
        </Label>
        {typeof value === 'string' ? (
          <p
            className={cn(
              'font-normal m-0 max-w-full break-words whitespace-pre-wrap overflow-hidden',
              small
                ? 'text-sm sm:text-base'
                : 'text-sm sm:text-lg leading-5 sm:leading-6',
              'text-foreground',
              ellipsis && 'truncate'
            )}
            title={value}
          >
            {displayedValue}
          </p>
        ) : (
          <div className="text-foreground flex items-center min-h-[40px]">
            {displayedValue}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(Info);
